/**
 * Product Quote Request Dialog Component
 * Handles the request a quote functionality using EmailJS
 */

class ProductQuoteDialog {
  constructor(trigger) {
    this.trigger = trigger;
    this.dialog = null;
    this.form = null;

    // EmailJS configuration - you'll need to update these with your actual values
    this.config = {
      publicKey: "PreDcsoCs5-KXWKE1",
      serviceId: "service_5tqyrkt",
      templateId: "template_i1o38s1",
    };

    // Rate limiting (5 quotes per hour per user)
    this.rateLimitKey = "quote_rate_limit";
    this.maxQuotes = 5;
    this.timeWindow = 3600000; // 1 hour

    this.init();
  }

  async init() {
    // Load EmailJS SDK if not already loaded
    if (typeof emailjs === "undefined") {
      await this.loadEmailJS();
    }

    // Initialize EmailJS
    emailjs.init(this.config.publicKey);

    this.createDialog();
    this.bindEvents();
  }

  loadEmailJS() {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  createDialog() {
    // Create dialog HTML
    const dialogHTML = `
      <dialog class="product-quote-dialog" aria-labelledby="quote-dialog-title" aria-modal="true">
        <div class="product-quote-dialog__content">
          <header class="product-quote-dialog__header">
            <h3 id="quote-dialog-title" class="product-quote-dialog__title">Request a Quote</h3>
            <button type="button" class="product-quote-dialog__close" aria-label="Close dialog">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </header>

          <form class="product-quote-form">
            <div class="product-quote-form__field">
              <label for="quote-name" class="product-quote-form__label">Your Name *</label>
              <input
                type="text"
                id="quote-name"
                name="customerName"
                class="product-quote-form__input"
                required
                placeholder="Your name"
              />
            </div>

            <div class="product-quote-form__field">
              <label for="quote-email" class="product-quote-form__label">Your Email *</label>
              <input
                type="email"
                id="quote-email"
                name="customerEmail"
                class="product-quote-form__input"
                required
                placeholder="your@email.com"
              />
            </div>

            <div class="product-quote-form__field">
              <label for="quote-phone" class="product-quote-form__label">Phone Number (optional)</label>
              <input
                type="tel"
                id="quote-phone"
                name="customerPhone"
                class="product-quote-form__input"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div class="product-quote-form__field">
              <label for="quote-subject" class="product-quote-form__label">Subject *</label>
              <input
                type="text"
                id="quote-subject"
                name="subject"
                class="product-quote-form__input"
                required
                readonly
              />
            </div>

            <div class="product-quote-form__field">
              <label for="quote-body" class="product-quote-form__label">Notes / Additional Information</label>
              <textarea
                id="quote-body"
                name="message"
                class="product-quote-form__textarea"
                rows="6"
                placeholder="Please provide any additional information about your quote request..."
              ></textarea>
              <div class="product-quote-form__char-counter">
                <span class="char-count">0</span>/500 characters
              </div>
            </div>

            <div class="product-quote-form__actions">
              <button type="button" class="product-quote-form__button product-quote-form__button--secondary">
                Cancel
              </button>
              <button type="submit" class="product-quote-form__button product-quote-form__button--primary">
                Send Quote Request
              </button>
            </div>
          </form>
        </div>
      </dialog>
    `;

    // Insert dialog into document
    document.body.insertAdjacentHTML("beforeend", dialogHTML);
    this.dialog = document.querySelector(".product-quote-dialog");
    this.form = this.dialog?.querySelector(".product-quote-form");

    // Set product data and subject
    this.setProductData();
    this.setupCharacterCounter();
  }

  setupCharacterCounter() {
    const textarea = this.form?.querySelector("#quote-body");
    const counter = this.form?.querySelector(".char-count");
    const maxLength = 500;

    if (textarea && counter) {
      const count = () => {
        const length = textarea.value.length;
        counter.textContent = Math.min(length, maxLength);
        if (length > maxLength) {
          textarea.value = textarea.value.substring(0, maxLength);
        }
      };

      textarea.addEventListener("input", count);
      textarea.addEventListener("paste", () => setTimeout(count, 10));
    }
  }

  setProductData() {
    if (!this.form) return;

    // Get product information from the page
    const productData = {
      title: this.getProductTitle(),
      url: window.location.href,
      price: this.getProductPrice(),
      image: this.getProductImage(),
      variant: this.getSelectedVariant(),
    };

    // Set the subject field with product name
    const subjectField = this.form.querySelector("#quote-subject");
    if (subjectField) {
      subjectField.value = `Quote Request: ${productData.title}`;
    }

    // Store product data for later use
    this.productData = productData;
  }

  getProductTitle() {
    return (
      document.querySelector("[data-product-title]")?.textContent ||
      document.querySelector("h1")?.textContent ||
      document.querySelector(".product__title")?.textContent ||
      document.querySelector(".product-title")?.textContent ||
      "Product"
    );
  }

  getProductPrice() {
    return (
      document.querySelector("[data-product-price]")?.textContent ||
      document.querySelector(".price")?.textContent ||
      document.querySelector(".product__price")?.textContent ||
      "Price on request"
    );
  }

  getProductImage() {
    let imageUrl = "";

    // Try data attribute first
    const dataImage = document.querySelector("[data-product-image]");
    if (dataImage) {
      imageUrl =
        dataImage.getAttribute("src") || dataImage.getAttribute("data-src");
      if (imageUrl) {
        console.log("Found image via data-product-image:", imageUrl);
        return this.sanitizeImageUrl(imageUrl);
      }
    }

    // Try product image selectors
    const productImg = document.querySelector(
      '.product__image img, .product-image img, img[alt*="product" i]',
    );
    if (productImg) {
      imageUrl =
        productImg.getAttribute("src") || productImg.getAttribute("data-src");
      if (imageUrl) {
        console.log("Found image via product selectors:", imageUrl);
        return this.sanitizeImageUrl(imageUrl);
      }
    }

    // Try Shopify specific selectors
    const shopifyImg = document.querySelector(
      ".featured-image img, .product-media img, .product-gallery img",
    );
    if (shopifyImg) {
      imageUrl =
        shopifyImg.getAttribute("src") || shopifyImg.getAttribute("data-src");
      if (imageUrl) {
        console.log("Found image via Shopify selectors:", imageUrl);
        return this.sanitizeImageUrl(imageUrl);
      }
    }

    // Try gallery images
    const galleryImg = document.querySelector(
      ".gallery img, .slider img, .carousel img",
    );
    if (galleryImg) {
      imageUrl =
        galleryImg.getAttribute("src") || galleryImg.getAttribute("data-src");
      if (imageUrl) {
        console.log("Found image via gallery selectors:", imageUrl);
        return this.sanitizeImageUrl(imageUrl);
      }
    }

    // Fallback: try any image on the page that might be product-related
    const images = document.querySelectorAll("img");
    for (const img of images) {
      const src = img.getAttribute("src") || img.getAttribute("data-src");
      if (src && (src.includes("product") || src.includes("cdn.shopify"))) {
        console.log("Found fallback image:", src);
        return this.sanitizeImageUrl(src);
      }
    }

    console.log("No product image found");
    return "";
  }

  sanitizeImageUrl(imageUrl) {
    if (!imageUrl) return "";

    console.log("Original image URL:", imageUrl);

    try {
      let cleanUrl = imageUrl;

      // Remove localhost/development URLs
      if (cleanUrl.includes("127.0.0.1") || cleanUrl.includes("localhost")) {
        console.log("Removing localhost from URL");
        // Try to extract just the path from Shopify CDN URLs
        const pathMatch = cleanUrl.match(
          /\/\/cdn\.shopify\.com\/s\/files\/[^"'\s]+/,
        );
        if (pathMatch) {
          cleanUrl = "https:" + pathMatch[0];
        } else {
          return ""; // Skip localhost images that aren't CDN URLs
        }
      }

      // Handle relative URLs by making them absolute
      if (cleanUrl.startsWith("//")) {
        cleanUrl = "https:" + cleanUrl;
      } else if (cleanUrl.startsWith("/") && !cleanUrl.startsWith("//")) {
        // For relative paths, try to construct with current domain
        const cdnPathMatch = cleanUrl.match(/\/cdn\.shopify\.com/);
        if (cdnPathMatch) {
          cleanUrl = "https:" + cleanUrl;
        } else {
          cleanUrl = window.location.origin + cleanUrl;
        }
      }

      // Ensure URL starts with https://
      if (!cleanUrl.startsWith("http")) {
        cleanUrl = "https://" + cleanUrl.replace(/^\/\//, "");
      }

      // Convert HTTP to HTTPS for security
      cleanUrl = cleanUrl.replace(/^http:\/\//, "https://");

      // Remove any query parameters that might cause issues
      try {
        const url = new URL(cleanUrl);
        // Keep only essential Shopify parameters
        const allowedParams = ["v", "width", "height"];
        const newParams = new URLSearchParams();

        allowedParams.forEach((param) => {
          if (url.searchParams.has(param)) {
            newParams.set(param, url.searchParams.get(param));
          }
        });

        url.search = newParams.toString();
        cleanUrl = url.toString();
      } catch (e) {
        console.log("URL parsing failed, using as-is:", e);
      }

      console.log("Sanitized image URL:", cleanUrl);
      return cleanUrl;
    } catch (error) {
      console.error("Error sanitizing image URL:", error);
      return "";
    }
  }

  getSelectedVariant() {
    // Try to get selected variant information
    const variantSelect = document.querySelector('[name="id"]');
    if (variantSelect && variantSelect.options) {
      const selectedOption = variantSelect.options[variantSelect.selectedIndex];
      return {
        id: variantSelect.value || "",
        title: selectedOption?.textContent || "",
      };
    }

    // Fallback: look for variant picker buttons
    const selectedVariantButton = document.querySelector(
      '.variant-picker__button[aria-pressed="true"]',
    );
    return {
      id: selectedVariantButton?.dataset?.variantId || "",
      title: selectedVariantButton?.textContent || "",
    };
  }

  bindEvents() {
    // Open dialog when trigger is clicked
    this.trigger.addEventListener("click", (e) => {
      e.preventDefault();
      this.openDialog();
    });

    // Close dialog events
    const closeButton = this.dialog?.querySelector(
      ".product-quote-dialog__close",
    );
    const cancelButton = this.dialog?.querySelector(
      ".product-quote-form__button--secondary",
    );

    closeButton?.addEventListener("click", () => this.closeDialog());
    cancelButton?.addEventListener("click", () => this.closeDialog());

    // Close on backdrop click
    this.dialog?.addEventListener("click", (e) => {
      if (e.target === this.dialog) {
        this.closeDialog();
      }
    });

    // Close on Escape key
    this.dialog?.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.closeDialog();
      }
    });

    // Handle form submission
    this.form?.addEventListener("submit", (e) => {
      this.handleSubmit(e);
    });
  }

  openDialog() {
    // Update product data in case variant changed
    this.setProductData();

    // Show dialog
    if (this.dialog && typeof this.dialog.showModal === "function") {
      this.dialog.showModal();
    }
    document.body.classList.add("dialog-open");

    // Focus first input
    const firstInput = this.dialog?.querySelector('input[type="text"]');
    if (firstInput && typeof firstInput.focus === "function") {
      firstInput.focus();
    }
  }

  closeDialog() {
    if (this.dialog && typeof this.dialog.close === "function") {
      this.dialog.close();
    }
    document.body.classList.remove("dialog-open");

    // Reset form (but preserve the product data)
    if (this.form && typeof this.form.reset === "function") {
      this.form.reset();
      // Re-set product data after reset
      setTimeout(() => {
        this.setProductData();
        // Reset character counter
        const counter = this.form?.querySelector(".char-count");
        if (counter) counter.textContent = "0";
      }, 100);
    }
  }

  checkRateLimit() {
    const now = Date.now();
    const rateLimitData = this.getRateLimitData();

    // Clean old entries
    const validEntries = rateLimitData.filter(
      (timestamp) => now - timestamp < this.timeWindow,
    );

    if (validEntries.length >= this.maxQuotes) {
      const oldestEntry = Math.min(...validEntries);
      const resetTime = Math.ceil(
        (oldestEntry + this.timeWindow - now) / 1000 / 60,
      );

      return {
        allowed: false,
        resetTime: resetTime,
      };
    }

    return { allowed: true };
  }

  getRateLimitData() {
    try {
      const data = localStorage.getItem(this.rateLimitKey);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  updateRateLimit() {
    const now = Date.now();
    const rateLimitData = this.getRateLimitData();
    rateLimitData.push(now);

    // Keep only recent entries
    const validEntries = rateLimitData.filter(
      (timestamp) => now - timestamp < this.timeWindow,
    );

    try {
      localStorage.setItem(this.rateLimitKey, JSON.stringify(validEntries));
    } catch {
      // Storage full, clear old data
      localStorage.removeItem(this.rateLimitKey);
    }
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;

    // Block obvious fake/disposable emails
    const disposableProviders = [
      "10minutemail",
      "tempmail",
      "guerrillamail",
      "mailinator",
    ];

    const domain = email.split("@")[1]?.toLowerCase();
    return !disposableProviders.some((provider) => domain?.includes(provider));
  }

  async handleSubmit(event) {
    event.preventDefault();

    // Check rate limit
    const rateCheck = this.checkRateLimit();
    if (!rateCheck.allowed) {
      this.showRateLimitError(rateCheck.resetTime);
      return;
    }

    if (!this.form) return;
    const formData = new FormData(this.form);

    // Validate email
    const customerEmail = formData.get("customerEmail");
    if (!this.validateEmail(customerEmail)) {
      this.showError("Please enter a valid email address.");
      return;
    }

    const templateParams = {
      customer_name: formData.get("customerName"),
      customer_email: customerEmail,
      customer_phone: formData.get("customerPhone") || "Not provided",
      subject: formData.get("subject"),
      message: formData.get("message") || "No additional information provided",
      product_title: this.productData.title,
      product_price: this.productData.price,
      product_url: this.productData.url,
      product_image: this.sanitizeImageUrl(this.productData.image),
      variant_info: this.productData.variant.title || "Default variant",
      shop_name: "Tovaa", // Replace with your actual store name
      reply_to: customerEmail,
    };

    const submitBtn = this.form?.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.textContent = "Sending...";
      submitBtn.disabled = true;
    }

    // Debug logging
    console.log("EmailJS Config:", this.config);
    console.log("Template Parameters:", templateParams);
    console.log("Customer Email:", templateParams.customer_email);
    console.log("Product Info:", {
      title: this.productData.title,
      price: this.productData.price,
      image: this.productData.image,
      sanitized_image: templateParams.product_image,
    });

    try {
      const result = await emailjs.send(
        this.config.serviceId,
        this.config.templateId,
        templateParams,
      );

      console.log("EmailJS Success:", result);

      // Update rate limit
      this.updateRateLimit();

      this.showSuccessMessage("Quote request sent successfully!");
      this.closeDialog();
    } catch (error) {
      console.error("EmailJS error:", error);
      console.error("Template params:", templateParams);

      let errorMessage = "Failed to send quote request. Please try again.";
      if (error.status === 422) {
        errorMessage =
          "Email configuration error. Please check your email address.";
      } else if (error.status === 402) {
        errorMessage =
          "Service temporarily unavailable. Please try again later.";
      } else if (error.status === 400) {
        errorMessage = "Invalid email address. Please check and try again.";
      }

      this.showError(errorMessage);
    } finally {
      if (submitBtn) {
        submitBtn.textContent = "Send Quote Request";
        submitBtn.disabled = false;
      }
    }
  }

  showSuccessMessage(message) {
    this.showNotification(message, "success");
  }

  showError(message) {
    this.showNotification(message, "error");
  }

  showRateLimitError(resetTimeMinutes) {
    const notice = this.form?.querySelector(".rate-limit-notice");
    const text = `Too many requests. Please wait ${resetTimeMinutes} minutes before trying again.`;

    if (notice) {
      notice.textContent = text;
      notice.style.display = "block";
    } else {
      this.showNotification(text, "error");
    }
  }

  showNotification(message, type = "info") {
    // Create temporary notification message
    const notification = document.createElement("div");
    notification.className = `product-quote-notification product-quote-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === "error" ? "#dc2626" : type === "success" ? "#16a34a" : "#000"};
      color: white;
      padding: 12px 24px;
      border-radius: 0px;
      z-index: 10000;
      animation: slideIn 0.3s ease-out;
      max-width: 300px;
      word-wrap: break-word;
    `;

    document.body.appendChild(notification);

    // Remove notification after 4 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.style.animation = "slideOut 0.3s ease-in";
        setTimeout(() => {
          if (notification.parentNode) {
            notification.remove();
          }
        }, 300);
      }
    }, 4000);
  }
}

// CSS Styles for the quote dialog (updated with character counter styles)
const quoteDialogStyles = `
  <style>
    .product-quote-dialog {
      border: none;
      border-radius: 0px;
      padding: 0;
      max-width: 600px;
      width: 90vw;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
      background: var(--color-background, #fff);
      color: var(--color-foreground, #000);
    }

    .product-quote-dialog::backdrop {
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(2px);
    }

    .product-quote-dialog__content {
      padding: 0;
    }

    .product-quote-dialog__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px 24px 16px;
      border-bottom: 1px solid var(--color-border, #e5e5e5);
    }

    .product-quote-dialog__title {
      margin: 0;
      font-size: 1.25rem;
    }

    .product-quote-dialog__close {
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px;
      border-radius: 0px;
      color: var(--color-foreground, #000);
      transition: background-color 0.2s ease;
    }

    .product-quote-dialog__close:hover {
      background: var(--color-background-secondary, #f5f5f5);
    }

    .product-quote-form {
      padding: 24px;
    }

    .product-quote-form__field {
      margin-bottom: 20px;
    }

    .product-quote-form__label {
      display: block;
      margin-bottom: 6px;
      font-weight: 500;
      color: var(--color-foreground, #000);
    }

    .product-quote-form__input,
    .product-quote-form__textarea {
      width: 100%;
      padding: 12px 16px;
      border: 1px solid var(--color-border, #d1d5db);
      border-radius: 0px;
      font-size: 16px;
      font-family: inherit;
      background: var(--color-background, #fff);
      color: var(--color-foreground, #000);
      transition: border-color 0.2s ease, box-shadow 0.2s ease;
      box-sizing: border-box;
    }

    .product-quote-form__input:focus,
    .product-quote-form__textarea:focus {
      outline: none;
      border-color: var(--color-primary, #000);
      box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(0, 0, 0, 0.1));
    }

    .product-quote-form__input[readonly] {
      background: var(--color-background-secondary, #f5f5f5);
      cursor: not-allowed;
    }

    .product-quote-form__textarea {
      resize: vertical;
      min-height: 120px;
    }

    .product-quote-form__char-counter {
      margin-top: 4px;
      font-size: 12px;
      color: var(--color-foreground-secondary, #666);
      text-align: right;
    }

    .product-quote-form__actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 24px;
      padding-top: 20px;
    }

    .product-quote-form__button {
      padding: 12px 24px;
      border: none;
      border-radius: 0px;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;
      min-width: 120px;
      text-transform: uppercase;
    }

    .product-quote-form__button:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .product-quote-form__button--primary {
      background: var(--color-primary, #000);
      color: var(--color-primary-text, #fff);
    }

    .product-quote-form__button--primary:hover:not(:disabled) {
      background: var(--color-primary-hover, #333);
    }

    .product-quote-form__button--secondary {
      background: white;
      color: var(--color-foreground, #000);
      border: 1px solid var(--color-border, #d1d5db);
    }

    .product-quote-form__button--secondary:hover {
      background: var(--color-background-tertiary, #e5e5e5);
    }

    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    @keyframes slideOut {
      from {
        transform: translateX(0);
        opacity: 1;
      }
      to {
        transform: translateX(100%);
        opacity: 0;
      }
    }

    @media (max-width: 640px) {
      .product-quote-dialog {
        width: 95vw;
        margin: 10px;
      }

      .product-quote-form__actions {
        flex-direction: column;
      }

      .product-quote-form__button {
        width: 100%;
      }
    }
  </style>
`;

// Initialize quote dialogs when DOM is loaded
document.addEventListener("DOMContentLoaded", function () {
  // Add styles to head
  document.head.insertAdjacentHTML("beforeend", quoteDialogStyles);

  // Initialize all quote buttons
  const quoteButtons = document.querySelectorAll("[data-quote-trigger]");
  quoteButtons.forEach((button) => {
    new ProductQuoteDialog(button);
  });
});

// Export for use in modules if needed
if (typeof module !== "undefined" && module.exports) {
  module.exports = ProductQuoteDialog;
}
